/**
 * Freight amount keyed on an order (freight + fuel surcharge).
 *
 * Rendered inside the Order tab's Freight row on EVERY order that is not
 * cancelled, drafts included (owner ruling 2026-10-07: whatever the CSR
 * keys at any stage is the truth). Two flavours, chosen by `truckload`:
 *   - truckload/logistics carrier (RYAN TRANSPORT, ECHO, KAG, TQL, ...):
 *     Sage has no live rate; the broker quotes a number by email and the
 *     desk keys it here so it is on file at BOL scan.
 *   - any other carrier (SAIA, ESTES, PITT, BEST WAY, ...): the amount is an
 *     OVERRIDE — the sales site bills it instead of the carrier's live quote.
 * Either way it is pushed to the sales site once the order has an AU#, and
 * the row shows whether that push landed (`summary.last_push`).
 *
 * Also the entry point while a scanned BOL is waiting for freight
 * (`needsInput`): the sales site holds the ship until an amount is keyed, and
 * saving one re-runs the shipping flow automatically (kanban 5c619a16).
 *
 * And for ANY carrier while the sales site's "Freight Error For Order #"
 * notice is open (`freightError`, kanban 28903894): the gun scan shipped the
 * order at $0 freight and the bell alert sent the desk here to key the
 * amount. The sales site refuses amounts after its scan, so the control is
 * honest that the Sage invoice itself still has to carry the number before
 * the batch posts.
 *
 * States: empty (Set button), set (billed total + Edit + History), editing
 * (freight / fuel / note form), locked (invoiced — read-only).
 *
 * The freight the CSR keys is the amount billed. Fuel is OPTIONAL and added
 * on top; it is never filled in on its own. A silent 10% fuel default billed
 * AU02377 at $1,980 on a keyed $1,800 (Emily, 2026-10-14, kanban 4dfbe790).
 * Owner ruling on review mk5qof: fuel may be SUGGESTED but the CSR has to
 * confirm it, so the form offers a "Use suggested 10% fuel" button that
 * fills the field only when clicked, and says "Will bill $X" before Save.
 *
 * The parent owns the order detail; after a successful save the fresh
 * summary from the response is handed back via `onChanged` so the row
 * updates without a reload.
 */
import React, { useEffect, useState } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import type {
  FreightErrorInfo,
  FreightNeedsInputInfo,
  TruckloadFreightHistoryEntry,
  TruckloadFreightSummary,
} from '../../services/portalOrders';

/** Fuel the warehouse keys in practice on broker-quoted truckload freight.
 *  Offered as a one-click suggestion, never applied without the click. */
const SUGGESTED_FUEL_SURCHARGE_RATE = 0.1;

/** 10% of freight, rounded to cents. */
function suggestedFuelSurcharge(freight: number): number {
  return Math.round(freight * SUGGESTED_FUEL_SURCHARGE_RATE * 100) / 100;
}

function toMoneyString(value: number): string {
  return value.toFixed(2);
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export interface TruckloadFreightControlProps {
  orderId: string;
  /** orders:manage viewer — hides Set/Edit when false. */
  canEdit: boolean;
  /** Invoiced orders are read-only (the backend refuses edits too). */
  locked?: boolean;
  /** ship_via is a truckload/logistics carrier (no live rate). False = the
   *  amount overrides the carrier's live quote. Defaults to true. */
  truckload?: boolean;
  summary: TruckloadFreightSummary | null;
  /** Set when a scanned BOL is waiting for this freight — the ship is held
   *  until an amount is saved, whatever the carrier (kanban 5c619a16). */
  needsInput?: FreightNeedsInputInfo | null;
  /** Set when the watchdog mirrored a sales-site "Freight Error" notice for
   *  this order — the invoice was built with $0 freight at the gun scan and
   *  the desk keys the amount here (kanban 28903894). */
  freightError?: FreightErrorInfo | null;
  formatPrice: (val: string | number | null | undefined) => string;
  onChanged: (summary: TruckloadFreightSummary | null) => void;
}

const muted: React.CSSProperties = { color: '#6b7280', fontSize: '12px' };
const smallBtn: React.CSSProperties = {
  background: 'none',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#374151',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const input: React.CSSProperties = {
  width: '110px',
  padding: '3px 6px',
  fontSize: '13px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
};

const TruckloadFreightControl: React.FC<TruckloadFreightControlProps> = ({
  orderId,
  canEdit,
  locked = false,
  truckload = true,
  summary,
  needsInput = null,
  freightError = null,
  formatPrice,
  onChanged,
}) => {
  const mode = needsInput
    ? 'held_scan'
    : freightError
      ? 'freight_error'
      : truckload
        ? 'truckload'
        : 'override';
  const [editing, setEditing] = useState(false);
  const [freightRaw, setFreightRaw] = useState('');
  const [fuelRaw, setFuelRaw] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<TruckloadFreightHistoryEntry[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Reset local state when the modal switches orders.
  useEffect(() => {
    setEditing(false);
    setMessage(null);
    setShowHistory(false);
    setHistory(null);
    setHistoryError(null);
  }, [orderId]);

  const openForm = () => {
    if (summary) {
      setFreightRaw(toMoneyString(Number(summary.freight)));
      // A saved $0 fuel shows as an empty optional field, not "0.00".
      setFuelRaw(Number(summary.fuel) > 0 ? toMoneyString(Number(summary.fuel)) : '');
      setNote(summary.note ?? '');
    } else {
      setFreightRaw('');
      setFuelRaw('');
      setNote('');
    }
    setMessage(null);
    setEditing(true);
    trackEvent('action', summary ? 'freight_preset_edit_open' : 'freight_preset_set_open', {
      order_id: orderId,
      mode,
      needs_input: needsInput != null,
      freight_error: freightError != null,
    });
  };

  const handleSave = async () => {
    if (busy) return;
    const freight = parseAmount(freightRaw);
    if (freight == null || freight <= 0) {
      setMessage({ kind: 'error', text: 'Enter a freight amount greater than zero.' });
      return;
    }
    const fuel = fuelRaw.trim() === '' ? null : parseAmount(fuelRaw);
    if (fuelRaw.trim() !== '' && (fuel == null || fuel < 0)) {
      setMessage({ kind: 'error', text: 'Fuel surcharge must be zero or more.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const resp = await portalOrderService.setTruckloadFreight(orderId, {
        freight_amount: toMoneyString(freight),
        fuel_surcharge_amount: fuel == null ? null : toMoneyString(fuel),
        note: note.trim() === '' ? null : note.trim(),
      });
      // The automatic bolScanProcess re-call, when a scan was waiting.
      const resubmit = resp.scan_resubmit ?? null;
      trackEvent('action', 'freight_preset_save', {
        order_id: orderId,
        mode,
        needs_input: needsInput != null,
        freight_error: freightError != null,
        outcome: resp.outcome,
        succeeded: resp.succeeded,
        push_status: resp.push?.status ?? null,
        resubmit_status: resubmit?.status ?? null,
      });
      if (resp.succeeded) {
        onChanged(resp.truckload_freight);
        setEditing(false);
        setHistory(null); // stale — refetch on next open
        const pushText = resp.push ? ` ${resp.push.message}` : '';
        const resubmitText = resubmit ? ` ${resubmit.message}` : '';
        const pushFailed =
          !!resp.push && resp.push.status !== 'applied' && resp.push.status !== 'applied_test';
        const resubmitFailed = !!resubmit && resubmit.status === 'failed';
        setMessage({
          kind: pushFailed || resubmitFailed ? 'error' : 'ok',
          text: `${resp.message}${pushText}${resubmitText}`,
        });
      } else {
        setMessage({ kind: 'error', text: resp.message });
      }
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
          : undefined;
      setMessage({
        kind: 'error',
        text: typeof detail === 'string' ? detail : 'Failed to save truckload freight. Please retry.',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    trackEvent('action', 'freight_preset_history_toggle', { order_id: orderId, open: next });
    if (next && history == null) {
      setHistoryError(null);
      try {
        setHistory(await portalOrderService.getTruckloadFreightHistory(orderId));
      } catch {
        setHistoryError('Could not load history.');
      }
    }
  };

  const editable = canEdit && !locked;
  // Both asks mean "this order has no freight, enter it" — same amber button.
  const freightAsked = needsInput != null || freightError != null;
  const emptyText = locked
    ? freightError
      ? 'Invoice already posted with $0 freight — bill it with a freight-only invoice'
      : 'No freight amount was keyed in the portal'
    : needsInput
      ? 'Freight needed — the scanned BOL is waiting on this amount'
      : freightError
        ? 'Freight needed — the invoice was built with $0 at BOL scan'
        : truckload
          ? 'Truckload — no freight amount on file'
          : 'No override — Sage bills the carrier quote at BOL scan';
  const lastPush = summary?.last_push ?? null;
  const pushLanded = lastPush?.status === 'applied' || lastPush?.status === 'applied_test';
  // What the form will bill: freight as keyed, plus fuel only when keyed.
  const formFreight = parseAmount(freightRaw) ?? 0;
  const formFuel = fuelRaw.trim() === '' ? 0 : parseAmount(fuelRaw) ?? 0;
  const formTotal = formFreight + formFuel;
  const summaryFuel = summary ? Number(summary.fuel) : 0;
  // Offered only while the fuel field is empty; clicking it is the CSR's confirmation.
  const fuelSuggestion = formFreight > 0 && fuelRaw.trim() === '' ? suggestedFuelSurcharge(formFreight) : null;

  const applyFuelSuggestion = () => {
    if (fuelSuggestion == null) return;
    setFuelRaw(toMoneyString(fuelSuggestion));
    trackEvent('action', 'freight_preset_fuel_suggestion_apply', {
      order_id: orderId,
      mode,
      freight: toMoneyString(formFreight),
      fuel: toMoneyString(fuelSuggestion),
    });
  };

  return (
    <div data-testid="truckload-freight-control" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {!editing && summary == null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span data-testid="truckload-freight-empty" style={muted}>
            {emptyText}
          </span>
          {editable && (
            <button
              type="button"
              onClick={openForm}
              style={
                freightAsked
                  ? { ...smallBtn, background: '#b45309', color: '#fff', border: '1px solid #b45309' }
                  : smallBtn
              }
              data-testid="truckload-freight-set-btn"
            >
              {freightAsked ? 'Enter freight' : truckload ? 'Set truckload freight' : 'Set freight'}
            </button>
          )}
        </div>
      )}

      {!editing && summary != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span data-testid="truckload-freight-value" title={summary.note ?? undefined}>
            <strong>{formatPrice(summary.total)}</strong>
            {summaryFuel > 0 && (
              <span style={{ marginLeft: 6 }}>
                ({formatPrice(summary.freight)} freight + {formatPrice(summary.fuel)} fuel)
              </span>
            )}
            <span style={{ ...muted, marginLeft: 6 }}>
              ({truckload ? 'truckload' : 'override — bills instead of the carrier quote'}
              {summary.set_by ? `, set by ${summary.set_by}` : ''}
              {summary.set_at ? ` ${shortDate(summary.set_at)}` : ''}
              {locked ? ', invoiced — locked' : ''})
            </span>
          </span>
          {editable && (
            <button type="button" onClick={openForm} style={smallBtn} data-testid="truckload-freight-edit-btn">
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={toggleHistory}
            style={{ ...smallBtn, border: 'none', textDecoration: 'underline', padding: 0 }}
            data-testid="truckload-freight-history-btn"
            aria-expanded={showHistory}
          >
            History ({summary.history_count})
          </button>
        </div>
      )}

      {!editing && summary?.note && (
        <div data-testid="truckload-freight-note" style={muted}>
          {summary.note}
        </div>
      )}

      {!editing && summary != null && lastPush && (
        <div
          data-testid="truckload-freight-push-status"
          style={{ ...muted, color: pushLanded ? '#1a7f37' : '#b45309' }}
          title={lastPush.http_status != null ? `HTTP ${lastPush.http_status}` : undefined}
        >
          {pushLanded ? 'Sales site: ' : 'Sales site: not applied — '}
          {lastPush.message}
        </div>
      )}

      {editing && (
        <form
          data-testid="truckload-freight-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '6px 0' }}
        >
          {mode === 'override' && (
            <div data-testid="truckload-freight-override-hint" style={muted}>
              This amount replaces the carrier&apos;s quote on the invoice and is billed exactly as keyed.
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', ...muted }}>
              Freight (amount to bill)
              <input
                type="text"
                inputMode="decimal"
                value={freightRaw}
                onChange={(e) => setFreightRaw(e.target.value)}
                placeholder="1800.00"
                style={input}
                data-testid="truckload-freight-input"
                autoFocus
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', ...muted }}>
              Fuel surcharge (optional, added on top)
              <input
                type="text"
                inputMode="decimal"
                value={fuelRaw}
                onChange={(e) => setFuelRaw(e.target.value)}
                placeholder="0.00"
                style={input}
                data-testid="truckload-fuel-input"
              />
              {fuelSuggestion != null && (
                <button
                  type="button"
                  onClick={applyFuelSuggestion}
                  style={{ ...smallBtn, border: 'none', textDecoration: 'underline', padding: 0, fontWeight: 400, color: '#1d4ed8' }}
                  data-testid="truckload-fuel-suggest-btn"
                >
                  Use suggested 10% fuel: {formatPrice(fuelSuggestion)}
                </button>
              )}
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', ...muted, flex: 1, minWidth: 160 }}>
              Note (broker, quote ref, attachment)
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ECHO quote — rate confirmation under Documents > Other"
                maxLength={1000}
                style={{ ...input, width: '100%' }}
                data-testid="truckload-freight-note-input"
              />
            </label>
          </div>
          <div
            data-testid="truckload-freight-will-bill"
            style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}
          >
            Will bill {formatPrice(formTotal)}
            {formFuel > 0 && (
              <span style={{ ...muted, fontWeight: 400, marginLeft: 6 }}>
                ({formatPrice(formFreight)} freight + {formatPrice(formFuel)} fuel)
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="submit"
              disabled={busy}
              style={{ ...smallBtn, background: '#1f2937', color: '#fff', border: '1px solid #1f2937', cursor: busy ? 'wait' : 'pointer' }}
              data-testid="truckload-freight-save"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setMessage(null);
              }}
              style={smallBtn}
              data-testid="truckload-freight-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {message && (
        <div
          data-testid="truckload-freight-message"
          style={{
            fontSize: '12px',
            color: message.kind === 'ok' ? 'var(--color-success, #1a7f37)' : 'var(--color-danger, #b42318)',
          }}
        >
          {message.text}
        </div>
      )}

      {showHistory && summary != null && (
        <div
          data-testid="truckload-freight-history"
          style={{
            marginTop: 2,
            padding: '6px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            background: '#f9fafb',
            fontSize: '12px',
          }}
        >
          {historyError && <div style={{ color: '#b42318' }}>{historyError}</div>}
          {!historyError && history == null && <div style={muted}>Loading…</div>}
          {history != null && history.length === 0 && <div style={muted}>No history.</div>}
          {history != null && history.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {history.map((h) => (
                <li key={h.preset_id} data-testid="truckload-freight-history-entry">
                  {formatPrice(h.total)}
                  {Number(h.fuel) > 0 && (
                    <>
                      {' '}({formatPrice(h.freight)} freight + {formatPrice(h.fuel)} fuel)
                    </>
                  )}
                  <span style={{ ...muted, marginLeft: 6 }}>
                    {h.set_by ? `${h.set_by}, ` : ''}
                    {shortDate(h.set_at)}
                    {h.superseded_at == null ? ' (current)' : ''}
                    {h.note ? ` — ${h.note}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TruckloadFreightControl;
